import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { SpricedFilterDialogComponent } from '../spriced-filter-dialog/spriced-filter-dialog.component';

interface NotificationRow {
  [key: string]: string;
}

interface NotificationColumn {
  key: string;
  label?: string;
  width?: number;
  type?: string;
}

interface NotificationsConfig {
  title?: string;
  dataUrl?: string;
  columns?: NotificationColumn[];
}

type SortDirection = 'asc' | 'desc' | 'none';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatIconModule, MatButtonModule],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
})
export class NotificationsComponent implements OnInit {
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);

  private readonly DEFAULT_CONFIG_FILE = 'notifications-config.json';
  private readonly DEFAULT_DATA_URL = 'assets/mock-data/notifications.json';

  title = 'Notifications';
  private dataUrl = this.DEFAULT_DATA_URL;

  displayedColumns: string[] = [];
  columnLabels: { [key: string]: string } = {};
  tableColumns: { key: string; label: string }[] = [];
  private dateColumns = new Set<string>();

  private allRows: NotificationRow[] = [];
  rows: NotificationRow[] = [];
  isLoading = true;
  hasDialogFilterApplied = false;
  selectedIndex = 0;
  pageSize = 50;
  currentPage = 1;

  private appliedDialogFilterPayload: any = null;
  private readonly DEFAULT_COL_WIDTH = 220;
  private readonly MIN_COL_WIDTH = 80;
  columnWidths: { [col: string]: number } = {};
  private resizingCol: string | null = null;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private suppressNextHeaderClick = false;

  sortKey: string | null = null;
  sortDirection: SortDirection = 'none';

  ngOnInit(): void {
    const configFile =
      (this.route.snapshot.data?.['configFile'] as string)?.trim() ||
      this.DEFAULT_CONFIG_FILE;

    this.http
      .get<NotificationsConfig>(`assets/dashboard-config/${configFile}`)
      .subscribe({
        next: (config) => {
          this.applyConfig(config);
          this.loadData();
        },
        error: () => {
          this.applyConfig(null);
          this.loadData();
        },
      });
  }

  private applyConfig(config: NotificationsConfig | null): void {
    this.title = config?.title?.trim() || this.title;
    this.dataUrl = config?.dataUrl?.trim() || this.dataUrl;

    const columns = Array.isArray(config?.columns) ? config!.columns! : [];
    this.displayedColumns = columns.map((col) => col.key);
    this.columnLabels = {};
    this.columnWidths = {};
    this.dateColumns = new Set<string>();

    for (const col of columns) {
      this.columnLabels[col.key] = col.label ?? col.key;
      this.columnWidths[col.key] = col.width ?? this.DEFAULT_COL_WIDTH;
      if (col.type === 'date') {
        this.dateColumns.add(col.key);
      }
    }

    this.tableColumns = this.displayedColumns.map((key) => ({
      key,
      label: this.columnLabels[key],
    }));
  }

  private loadData(): void {
    this.http.get<NotificationRow[]>(this.dataUrl).subscribe({
      next: (data) => {
        this.allRows = data ?? [];
        this.updateVisibleRows();
        this.isLoading = false;
      },
      error: () => {
        this.allRows = [];
        this.rows = [];
        this.isLoading = false;
      },
    });
  }

  isDateColumn(column: string): boolean {
    return this.dateColumns.has(column);
  }

  selectRow(index: number): void {
    this.selectedIndex = index;
  }

  openFilterDialog(): void {
    const dialogRef = this.dialog.open(SpricedFilterDialogComponent, {
      width: '60vw',
      maxWidth: '70vw',
      panelClass: 'spriced-filter-dialog-panel',
      data: {
        tableColumns: this.tableColumns,
        appliedFilterPayload: this.appliedDialogFilterPayload,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.appliedDialogFilterPayload = this.clonePayload(result);
      this.hasDialogFilterApplied = true;
      this.updateVisibleRows();
    });
  }

  clearFilterAction(): void {
    if (!this.hasDialogFilterApplied) {
      return;
    }

    this.appliedDialogFilterPayload = null;
    this.hasDialogFilterApplied = false;
    this.updateVisibleRows();
  }

  onResizeStart(event: MouseEvent, column: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.suppressNextHeaderClick = true;
    this.resizingCol = column;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.columnWidths[column] ?? this.DEFAULT_COL_WIDTH;
    if (typeof document !== 'undefined') {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.resizingCol) return;
    const delta = event.clientX - this.resizeStartX;
    const newWidth = Math.max(this.MIN_COL_WIDTH, this.resizeStartWidth + delta);
    this.columnWidths[this.resizingCol] = newWidth;
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    if (!this.resizingCol) return;
    this.resizingCol = null;
    if (typeof document !== 'undefined') {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    setTimeout(() => {
      this.suppressNextHeaderClick = false;
    }, 0);
  }

  onHeaderClick(column: string): void {
    if (this.suppressNextHeaderClick) {
      this.suppressNextHeaderClick = false;
      return;
    }

    if (this.sortKey !== column) {
      this.sortKey = column;
      this.sortDirection = 'asc';
    } else if (this.sortDirection === 'asc') {
      this.sortDirection = 'desc';
    } else if (this.sortDirection === 'desc') {
      this.sortDirection = 'none';
    } else {
      this.sortDirection = 'asc';
    }

    this.updateVisibleRows();
  }

  getSortIndicator(column: string): string {
    if (this.sortKey !== column || this.sortDirection === 'none') return '';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  private updateVisibleRows(): void {
    const filteredRows = this.getFilteredRows();

    if (!this.sortKey || this.sortDirection === 'none') {
      this.rows = [...filteredRows];
      this.syncSelectedIndex();
      return;
    }

    const key = this.sortKey;
    this.rows = [...filteredRows].sort((a, b) => this.compareByColumn(a, b, key));
    this.syncSelectedIndex();
  }

  private getFilteredRows(): NotificationRow[] {
    const filters = this.appliedDialogFilterPayload?.requestData?.filters;
    if (!Array.isArray(filters) || filters.length === 0) {
      return [...this.allRows];
    }

    return this.allRows.filter((row) => this.matchesFilterExpression(row, filters));
  }

  private syncSelectedIndex(): void {
    this.selectedIndex = this.rows.length === 0
      ? -1
      : Math.min(Math.max(this.selectedIndex, 0), this.rows.length - 1);
  }

  private compareByColumn(a: NotificationRow, b: NotificationRow, key: string): number {
    const left = (a as any)[key];
    const right = (b as any)[key];
    if (left === right) return 0;
    if (left === undefined || left === null || left === '') return 1;
    if (right === undefined || right === null || right === '') return -1;

    let result: number;
    if (this.isDateLike(left) && this.isDateLike(right)) {
      result = new Date(left).getTime() - new Date(right).getTime();
    } else {
      result = String(left).localeCompare(String(right), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }
    return this.sortDirection === 'desc' ? -result : result;
  }

  private isDateLike(value: any): boolean {
    if (typeof value !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}T/.test(value) && !Number.isNaN(Date.parse(value));
  }

  private matchesFilterExpression(row: NotificationRow, filters: any[]): boolean {
    const tokens: Array<boolean | string> = [];

    filters.forEach((filter: any) => {
      for (const marker of String(filter?.beginScope || '')) {
        if (marker === '(') {
          tokens.push('(');
        }
      }

      tokens.push(this.matchesSingleFilter(row, filter));

      for (const marker of String(filter?.endScope || '')) {
        if (marker === ')') {
          tokens.push(')');
        }
      }

      const logicalOperator = String(filter?.logicalOperator || '').trim().toUpperCase();
      if (logicalOperator === 'AND' || logicalOperator === 'OR') {
        tokens.push(logicalOperator);
      }
    });

    return this.evaluateBooleanExpression(tokens);
  }

  private evaluateBooleanExpression(tokens: Array<boolean | string>): boolean {
    let index = 0;

    const parseOr = (): boolean => {
      let value = parseAnd();
      while (tokens[index] === 'OR') {
        index++;
        value = value || parseAnd();
      }
      return value;
    };

    const parseAnd = (): boolean => {
      let value = parsePrimary();
      while (tokens[index] === 'AND') {
        index++;
        value = value && parsePrimary();
      }
      return value;
    };

    const parsePrimary = (): boolean => {
      const token = tokens[index];
      if (token === '(') {
        index++;
        const value = parseOr();
        if (tokens[index] === ')') {
          index++;
        }
        return value;
      }

      index++;
      return token === true;
    };

    return tokens.length ? parseOr() : true;
  }

  private matchesSingleFilter(row: NotificationRow, filter: any): boolean {
    const field = String(filter?.attributeName || '').trim() as keyof NotificationRow;
    const operator = String(filter?.operator || '').trim().toLowerCase();
    const rowValue = row[field];
    const filterValue = filter?.attributeValue;

    switch (operator) {
      case 'is_equal_to':
      case 'is equal':
        return this.areEqual(rowValue, filterValue);
      case 'is_not_equal_to':
      case 'is not equal':
        return !this.areEqual(rowValue, filterValue);
      case 'contains':
        return String(rowValue ?? '').toLowerCase().includes(String(filterValue ?? '').toLowerCase());
      case 'in':
        return this.toArray(filterValue).some((value) => this.areEqual(rowValue, value));
      case 'not_in':
        return !this.toArray(filterValue).some((value) => this.areEqual(rowValue, value));
      case 'greater_than':
      case 'greater than':
        return this.compareValues(rowValue, filterValue) > 0;
      case 'less_than':
      case 'less than':
        return this.compareValues(rowValue, filterValue) < 0;
      case 'greater than or equal':
      case 'greater_than_or_equal':
        return this.compareValues(rowValue, filterValue) >= 0;
      case 'less than or equal':
      case 'less_than_or_equal':
        return this.compareValues(rowValue, filterValue) <= 0;
      case 'is_null':
        return rowValue === null || rowValue === undefined || rowValue === '';
      case 'is_not_null':
        return rowValue !== null && rowValue !== undefined && rowValue !== '';
      default:
        return true;
    }
  }

  private areEqual(left: any, right: any): boolean {
    if (this.isDateValue(left) && this.isDateValue(right)) {
      return this.compareValues(left, right) === 0;
    }

    if (this.isNumericValue(left) && this.isNumericValue(right)) {
      return Number(left) === Number(right);
    }

    return String(left ?? '').trim().toLowerCase() === String(right ?? '').trim().toLowerCase();
  }

  private compareValues(left: any, right: any): number {
    if (this.isDateValue(left) && this.isDateValue(right)) {
      return new Date(left).getTime() - new Date(right).getTime();
    }

    if (this.isNumericValue(left) && this.isNumericValue(right)) {
      return Number(left) - Number(right);
    }

    return String(left ?? '').localeCompare(String(right ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  private isDateValue(value: any): boolean {
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }

    if (typeof value === 'string') {
      return !Number.isNaN(Date.parse(value));
    }

    return false;
  }

  private isNumericValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    return !Number.isNaN(Number(value));
  }

  private toArray(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '');
    }

    return value === null || value === undefined ? [] : [value];
  }

  private clonePayload(payload: any): any {
    return payload && typeof payload === 'object'
      ? JSON.parse(JSON.stringify(payload))
      : payload;
  }
}
